import { ReactElement, MouseEvent } from "react";
import cx from "clsx";
import { Box, Label } from "@twilio-paste/core";

import { copy, types } from "./definitions";
import { CompositePillGroupViewType } from "../compositePillGroup/definitions/types";
import CompositeInput from "../compositeInput";
import CompositePillGroup from "../compositePillGroup";
import TrackingButton from "../trackingButton";
import styles from "./CompositeInputFormPillGroup.module.css";

const CompositeInputFormPillGroup = ({
  id,
  disabled = false,
  width = "100%",
  placeholder = "",
  inputType = "text",
  label,
  errorText,
  helpText,
  emptyStateText,
  required = false,
  pills,
  hasError,
  marginBottom = "space80",
  value,
  onEdit,
  onCancel,
  onChange,
  onRemovePill,
  onSubmit,
  onValidate,
  viewType = CompositePillGroupViewType.display,
}: types.CompositePillInputProps): ReactElement => {
  const handleValidate = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();

    // Clean up the input value by removing leading and trailing whitespace
    let trimmedValue = value.trim();

    // Check if the cleaned value has a comma at the end
    const hasCommaAtEnd = trimmedValue.endsWith(",");

    // Remove the comma if it exists at the end of the string
    if (hasCommaAtEnd) {
      for (let i = trimmedValue.length - 1; i >= 0; i--) {
        if (trimmedValue[i] === ",") {
          trimmedValue = trimmedValue.slice(0, -1);
        } else {
          break;
        }
      }
    }

    // Check if the cleaned value has multiple values separated by commas
    const hasMultipleValues = trimmedValue.includes(",");

    const newPills = hasMultipleValues
      ? trimmedValue.split(",").map((pill) => pill.trim())
      : trimmedValue
      ? [trimmedValue]
      : [];

    const isValid = onValidate ? onValidate(newPills) : true;

    if (isValid) {
      handleSubmitPillValue(newPills);
    }
  };

  const handleSubmitPillValue = (newPills: string[]): void => {
    onSubmit(newPills);
  };

  const handleCancel = (): void => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleEdit = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();

    if (onEdit) {
      onEdit();
    }
  };

  const handleRemovePill = (removedPill: string): void => {
    const updatedPills = pills.filter((pill) => pill !== removedPill);

    onRemovePill(updatedPills);
  };

  const isUsingFormViewType = viewType === CompositePillGroupViewType.form;

  return (
    <Box
      as="form"
      marginBottom={marginBottom}
      className={cx([
        styles.compositeInputFormPillGroup,
        {
          [styles.compositeInputFormPillGroupDisplay]: !isUsingFormViewType,
          [styles.compositeInputFormPillGroupForm]: isUsingFormViewType,
        },
      ])}
      width={width}
    >
      <Box
        as="div"
        className={cx({
          [styles.compositeInputFormPillGroupContainerForm]:
            isUsingFormViewType,
        })}
      >
        {isUsingFormViewType && (
          <CompositeInput
            label={label}
            placeholder={placeholder}
            type={inputType}
            value={value}
            onChange={onChange}
            hasError={hasError}
            helpText={helpText}
            errorText={errorText}
            required={required}
            marginBottom="space30"
          />
        )}

        {!isUsingFormViewType && <Label htmlFor={id}>{label}</Label>}

        <CompositePillGroup
          id={id}
          label={label}
          pills={pills}
          emptyStateText={emptyStateText}
          viewType={viewType}
          onRemovePill={handleRemovePill}
        />
      </Box>

      <Box
        as="div"
        className={cx([
          styles.compositeInputFormPillFormButtonContainer,
          {
            [styles.compositeInputFormPillFormButtonContainerForm]:
              isUsingFormViewType,
          },
        ])}
      >
        {isUsingFormViewType ? (
          <TrackingButton
            variant="secondary"
            disabled={disabled}
            onClick={handleValidate}
            type="submit"
          >
            {!!pills.length ? copy.updateButtonValue : copy.addButtonValue}
          </TrackingButton>
        ) : (
          <TrackingButton
            variant="secondary"
            onClick={handleEdit}
            disabled={disabled}
            type="button"
          >
            {!!pills.length ? copy.editButtonValue : copy.addButtonValue}
          </TrackingButton>
        )}

        {isUsingFormViewType && onCancel && (
          <TrackingButton
            onClick={handleCancel}
            disabled={disabled}
            variant="link"
            size="reset"
            type="reset"
          >
            {copy.cancelButtonValue}
          </TrackingButton>
        )}
      </Box>
    </Box>
  );
};

export default CompositeInputFormPillGroup;
